import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Trash2, 
  AlertTriangle, 
  Settings, 
  Shield, 
  Clock, 
  CheckCircle2,
  XCircle,
  ArrowRight,
  Mail
} from "lucide-react";

function DeleteAccount() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-[#e0e0e0] px-4 py-10">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-900/20 border border-red-900/50">
            <Trash2 className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-white">Delete Your Account</h1>
          <p className="text-[#a0a0a0] max-w-xl mx-auto">
            <span className="font-semibold text-white">Soundope</span> owned by{" "}
            <span className="font-semibold text-white">Dead Entertainment LLC</span>{" "}
            respects your right to delete your personal data. This page explains how to 
            delete your account and what happens to your data.
          </p>
        </div>

        {/* Steps to Delete */}
        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
              How to Delete Your Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-[#a0a0a0]">
              Follow these steps to permanently delete your Soundope account:
            </p>
            
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-white font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-white">Sign in to your account</h3>
                  <p className="text-sm text-[#808080]">
                    Log in to Soundope using your email and password.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-white font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-white">Go to Settings</h3>
                  <p className="text-sm text-[#808080]">
                    Navigate to your Profile, then tap on Settings.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center text-white font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-white">Scroll to "Danger Zone"</h3>
                  <p className="text-sm text-[#808080]">
                    At the bottom of the Settings page, find the red "Danger Zone" section.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-900/50 border border-red-900 flex items-center justify-center text-white font-bold">
                  4
                </div>
                <div>
                  <h3 className="font-semibold text-white">Click "Delete Account"</h3>
                  <p className="text-sm text-[#808080]">
                    Click the Delete Account button and type "DELETE" to confirm. 
                    Your account and all data will be permanently removed.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[#1f1f1f]">
              <Button 
                onClick={() => navigate('/settings')}
                className="w-full sm:w-auto"
              >
                <Settings className="w-4 h-4 mr-2" />
                Go to Settings
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Data Deleted */}
        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <XCircle className="w-5 h-5" />
              Data That Will Be Deleted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#a0a0a0] mb-4">
              The following data is <span className="text-red-400 font-semibold">permanently deleted</span> when you delete your account:
            </p>
            <ul className="space-y-3">
              {[
                "Your profile information (username, artist name, bio, avatar)",
                "Email address and account credentials",
                "All tracks and music you have uploaded",
                "All feedback and reviews you have written",
                "All votes you have cast on other tracks",
                "Your credit balance (both earned and purchased)",
                "All messages sent and received",
                "All notifications",
                "Social links and playlist URLs",
                "Purchase and transaction history",
                "Promo code redemption history",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <Trash2 className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-[#c0c0c0]">{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Data Retained */}
        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle2 className="w-5 h-5" />
              Data That May Be Retained
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#a0a0a0] mb-4">
              Certain data may be retained for legal, security, or operational purposes:
            </p>
            <ul className="space-y-3">
              {[
                {
                  item: "Aggregated, anonymized analytics data",
                  reason: "Cannot be linked back to you"
                },
                {
                  item: "Transaction records for tax/legal compliance",
                  reason: "Retained for 7 years as required by law"
                },
                {
                  item: "Abuse/moderation reports you submitted",
                  reason: "Retained for platform safety"
                },
                {
                  item: "Server logs containing IP addresses",
                  reason: "Automatically deleted after 90 days"
                },
              ].map((data, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <Shield className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-[#c0c0c0]">{data.item}</span>
                    <p className="text-xs text-[#606060] mt-0.5">{data.reason}</p>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Retention Period */}
        <Card className="bg-[#121212] border border-[#1f1f1f]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-400">
              <Clock className="w-5 h-5" />
              Retention & Deletion Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="bg-[#0a0a0a] rounded-lg p-4 border border-[#1a1a1a]">
                <h4 className="font-semibold text-white mb-2">Immediate Deletion</h4>
                <p className="text-sm text-[#808080]">
                  Your profile, tracks, feedback, and most personal data are deleted 
                  immediately upon confirmation.
                </p>
              </div>
              <div className="bg-[#0a0a0a] rounded-lg p-4 border border-[#1a1a1a]">
                <h4 className="font-semibold text-white mb-2">Within 30 Days</h4>
                <p className="text-sm text-[#808080]">
                  Backup systems are purged of your data within 30 days of account deletion.
                </p>
              </div>
              <div className="bg-[#0a0a0a] rounded-lg p-4 border border-[#1a1a1a]">
                <h4 className="font-semibold text-white mb-2">90 Days</h4>
                <p className="text-sm text-[#808080]">
                  Server logs containing your IP address are automatically rotated and deleted.
                </p>
              </div>
              <div className="bg-[#0a0a0a] rounded-lg p-4 border border-[#1a1a1a]">
                <h4 className="font-semibold text-white mb-2">7 Years (Legal)</h4>
                <p className="text-sm text-[#808080]">
                  Financial transaction records are retained as required by tax regulations.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alternative Contact */}
        <Card className="bg-[#121212] border border-amber-900/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Can't Access Your Account?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-[#a0a0a0]">
              If you cannot access your account to delete it yourself, you can request 
              account deletion by contacting us directly:
            </p>
            <div className="bg-[#0a0a0a] rounded-lg p-4 border border-[#1a1a1a] space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <a 
                  href="mailto:support@soundope.com?subject=Account%20Deletion%20Request"
                  className="text-blue-400 hover:underline"
                >
                  support@soundope.com
                </a>
              </div>
              <p className="text-sm text-[#606060]">
                Please include the email address associated with your account. 
                We will verify your identity and process your request within 30 days.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <div className="text-center text-sm text-[#606060] space-y-2 pt-4 border-t border-[#1f1f1f]">
          <p>
            <span className="font-semibold text-[#808080]">Soundope</span> is owned and operated by{" "}
            <span className="font-semibold text-[#808080]">Dead Entertainment LLC</span>
          </p>
          <p>
            For privacy inquiries, please review our{" "}
            <button 
              onClick={() => navigate('/privacy')}
              className="text-blue-400 hover:underline"
            >
              Privacy Policy
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default DeleteAccount;
